import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createPage,
  getPageById,
  updatePage,
  deletePage,
  movePage,
  enablePublicSharing,
  disablePublicSharing,
  sharePageWithUser,
  getPublicPage
} from '../api/pageApi';
import { fetchPageById, updatePage as updatePageThunk } from '../redux/Slices/pageSlice';
import { showSuccess, showError } from '../utils/snackbarHelpers';

/**
 * Custom hooks for Notion-style page operations (without React Query)
 */

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch a page by ID
 */
export const usePageById = (pageId) => {
  const dispatch = useDispatch();
  const { loading: isLoading, error } = useSelector((state) => state.pages);

  useEffect(() => {
    if (!pageId) return;

    dispatch(fetchPageById({ pageId, useCache: true }));
  }, [pageId, dispatch]);

  return { 
    isLoading, 
    error, 
    refetch: () => dispatch(fetchPageById({ pageId, useCache: false })) 
  };
};

/**
 * Hook to fetch a public page
 */
export const usePublicPage = (pageId, token) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!pageId || !token) return;

    const fetchPage = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getPublicPage(pageId, token);
        setData(result);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPage();
  }, [pageId, token]);

  return { data, isLoading, error };
};

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to create a new page
 */
export const useCreatePage = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (pageData) => {
    setIsLoading(true);
    try {
      const result = await createPage(pageData);
      showSuccess(dispatch, 'Page created successfully');
      return result;
    } catch (error) {
      console.error('Error creating page:', error);
      showError(dispatch, error?.response?.data?.message || 'Failed to create page');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

/**
 * Hook to update a page
 */
export const useUpdatePage = () => {
  const dispatch = useDispatch();

  const mutateAsync = useCallback(async ({ pageId, updates }) => {
    try {
      const result = await dispatch(updatePageThunk({ pageId, updates })).unwrap();
      return result;
    } catch (error) {
      showError(dispatch, error || 'Failed to update page');
      throw error;
    }
  }, [dispatch]);

  return { mutateAsync };
};

/**
 * Hook to delete a page
 */
export const useDeletePage = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (pageId) => {
    setIsLoading(true);
    try {
      const result = await deletePage(pageId);
      showSuccess(dispatch, 'Page deleted successfully');
      return result;
    } catch (error) {
      showError(dispatch, error?.response?.data?.message || 'Failed to delete page');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

/**
 * Hook to move a page
 */
export const useMovePage = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async ({ pageId, moveData }) => {
    setIsLoading(true);
    try {
      const result = await movePage(pageId, moveData);
      showSuccess(dispatch, 'Page moved successfully');
      return result;
    } catch (error) {
      showError(dispatch, error?.response?.data?.message || 'Failed to move page');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

// ============================================
// Sharing Mutation Hooks
// ============================================

/**
 * Hook to enable public sharing
 */
export const useEnablePublicSharing = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (pageId) => {
    setIsLoading(true);
    try {
      const result = await enablePublicSharing(pageId);
      showSuccess(dispatch, 'Public sharing enabled');
      return result;
    } catch (error) {
      showError(dispatch, error?.response?.data?.message || 'Failed to enable public sharing');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

/**
 * Hook to disable public sharing
 */
export const useDisablePublicSharing = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async (pageId) => {
    setIsLoading(true);
    try {
      const result = await disablePublicSharing(pageId);
      showSuccess(dispatch, 'Public sharing disabled');
      return result;
    } catch (error) {
      showError(dispatch, error?.response?.data?.message || 'Failed to disable public sharing');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

/**
 * Hook to share page with a user
 */
export const useSharePageWithUser = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const mutateAsync = useCallback(async ({ pageId, shareData }) => {
    setIsLoading(true);
    try {
      const result = await sharePageWithUser(pageId, shareData);
      showSuccess(dispatch, 'Page shared successfully');
      return result;
    } catch (error) {
      showError(dispatch, error?.response?.data?.message || 'Failed to share page');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  return { mutateAsync, isLoading };
};

